import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { useRegionsByNation } from '@/hooks/useRegionsByNation'
import { useCohortsByNation } from '@/hooks/useCohortsByNation'
import { useDistrictsByNation } from '@/hooks/useDistrictsByNation'
import { AppLayout } from '@/components/layout/AppLayout'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { IdeologyDisplay } from '@/components/ideology/IdeologyDisplay'
import {
  KeyStatsCard,
  DemographicChart,
  DEMOGRAPHIC_COLORS,
  SalienceChart,
  RegionalBreakdown,
} from '@/components/demographics'
import {
  aggregateDemographics,
  aggregateIdeology,
  getSalienceData,
  calculateKeyStats,
  calculateRegionalSummaries,
  toChartData,
  formatAgeBracket,
  formatEducationLevel,
  formatIncomeLevel,
  formatSettlementType,
} from '@/lib/demographics'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { AgeBracket, EducationLevel, IncomeLevel, SettlementType } from '@/types'

export default function VoterDemographics() {
  const { id } = useParams<{ id: string }>()

  const { nation, isLoading: nationLoading, error: nationError } = useNation(id)
  const { world, isLoading: worldLoading } = useWorld(nation?.world_id)
  const { regions, isLoading: regionsLoading } = useRegionsByNation(id)
  const { cohorts, isLoading: cohortsLoading } = useCohortsByNation(id)
  const { districts, isLoading: districtsLoading } = useDistrictsByNation(id)

  const isLoading = nationLoading || worldLoading || regionsLoading || cohortsLoading || districtsLoading
  const error = nationError

  // Aggregate all demographic data
  const {
    ideology,
    salienceData,
    keyStats,
    regionalSummaries,
    chartData,
  } = useMemo(() => {
    if (!cohorts || !districts || !regions) {
      return {
        ideology: null,
        salienceData: [],
        keyStats: null,
        regionalSummaries: [],
        chartData: null,
      }
    }

    const demographicBreakdown = aggregateDemographics(cohorts, districts)
    const ideologyData = aggregateIdeology(cohorts)
    const salienceData = getSalienceData(ideologyData.averageSalience)
    const keyStats = calculateKeyStats(cohorts, districts, regions, demographicBreakdown, ideologyData)
    const regionalSummaries = calculateRegionalSummaries(cohorts, districts, regions)

    const chartData = {
      age: toChartData(
        demographicBreakdown.age,
        (key) => formatAgeBracket(key as AgeBracket),
        demographicBreakdown.totalPopulation
      ),
      education: toChartData(
        demographicBreakdown.education,
        (key) => formatEducationLevel(key as EducationLevel),
        demographicBreakdown.totalPopulation
      ),
      income: toChartData(
        demographicBreakdown.income,
        (key) => formatIncomeLevel(key as IncomeLevel),
        demographicBreakdown.totalPopulation
      ),
      settlement: toChartData(
        demographicBreakdown.settlement,
        (key) => formatSettlementType(key as SettlementType),
        demographicBreakdown.totalPopulation
      ),
      religion: toChartData(
        demographicBreakdown.religion,
        (key) => key || 'None',
        demographicBreakdown.totalPopulation
      ),
    }

    return { ideology: ideologyData, salienceData, keyStats, regionalSummaries, chartData }
  }, [cohorts, districts, regions])

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name, href: `/nations/${nation.id}` })
      items.push({ label: 'Demographics' })
    }
    return items
  }, [world, nation])

  // Loading skeleton
  if (isLoading) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div className="space-y-6">
          <div className="bg-muted h-8 w-64 animate-pulse rounded" />
          <div className="bg-muted h-5 w-48 animate-pulse rounded" />

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="bg-muted h-4 w-20 animate-pulse rounded mb-2" />
                  <div className="bg-muted h-8 w-16 animate-pulse rounded" />
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <div className="bg-muted h-5 w-32 animate-pulse rounded" />
                </CardHeader>
                <CardContent>
                  <div className="bg-muted h-48 animate-pulse rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </AppLayout>
    )
  }

  // Error state
  if (error) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error.message === 'JSON object requested, multiple (or no) rows returned'
            ? 'Nation not found.'
            : 'Failed to load demographics. Please try again later.'}
        </div>
      </AppLayout>
    )
  }

  // No data state
  if (!nation || !keyStats || !ideology || !chartData) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
          No demographic data available for this nation yet.
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <div className="mb-2 flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild className="-ml-2">
              <Link to={`/nations/${nation.id}`}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back
              </Link>
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Voter Demographics</h1>
            <Badge variant="outline">{nation.name}</Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            Understand the electorate: who they are, where they live, and what they care about
          </p>
        </div>

        {/* Key Stats */}
        <section>
          <h2 className="sr-only">Key Statistics</h2>
          <KeyStatsCard stats={keyStats} />
        </section>

        {/* Population Distribution Charts */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Population Distribution</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <DemographicChart
              title="Age Distribution"
              data={chartData.age}
              colors={DEMOGRAPHIC_COLORS.age}
            />
            <DemographicChart
              title="Education Level"
              data={chartData.education}
              colors={DEMOGRAPHIC_COLORS.education}
            />
            <DemographicChart
              title="Income Level"
              data={chartData.income}
              colors={DEMOGRAPHIC_COLORS.income}
            />
            <DemographicChart
              title="Settlement Type"
              data={chartData.settlement}
              colors={DEMOGRAPHIC_COLORS.settlement}
            />
            {chartData.religion.length > 0 && (
              <DemographicChart
                title="Religion"
                data={chartData.religion}
                colors={DEMOGRAPHIC_COLORS.religion}
              />
            )}
          </div>
        </section>

        {/* Issue Salience */}
        <section>
          <SalienceChart data={salienceData} />
        </section>

        {/* National Ideology Center */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">National Ideology Center</CardTitle>
              <CardDescription>
                Population-weighted average position across all voters. Individual cohorts may vary significantly.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <IdeologyDisplay ideology={ideology.averageVector} />
            </CardContent>
          </Card>
        </section>

        {/* Regional Breakdown */}
        <section>
          <RegionalBreakdown regions={regionalSummaries} />
        </section>
      </div>
    </AppLayout>
  )
}
